import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { useApp } from '../context/AppContext';
import StoriesBar from '../components/stories/StoriesBar';
import PostComposer from '../components/feed/PostComposer';
import PostCard from '../components/feed/PostCard';
import SponsoredCard from '../components/feed/SponsoredCard';
import SuggestedUsers from '../components/feed/SuggestedUsers';
import { Skeleton } from '../components/ui/skeleton';
import { Camera, Sparkles, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function FeedPage() {
  const { t } = useApp();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [postsRes, adsRes] = await Promise.all([
        api.get('/posts/feed'),
        api.get('/ads?placement=feed'),
      ]);
      setPosts(postsRes.data);
      setAds(adsRes.data);
    } catch (e) { /* handled by interceptor */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // listen for posts created via the global Create (+) dialog
  useEffect(() => {
    const handler = (e) => setPosts((p) => [e.detail, ...p]);
    window.addEventListener('fitcore:newpost', handler);
    return () => window.removeEventListener('fitcore:newpost', handler);
  }, []);

  const onNewPost = (post) => setPosts((p) => [post, ...p]);

  // interleave ads every 4 posts
  const items = [];
  posts.forEach((post, i) => {
    items.push({ type: 'post', data: post, key: post.id });
    if ((i + 1) % 4 === 0 && ads.length > 0) {
      const ad = ads[Math.floor(i / 4) % ads.length];
      items.push({ type: 'ad', data: ad, key: `ad-${ad.id}-${i}` });
    }
  });

  return (
    <div className="max-w-[1120px] mx-auto lg:grid lg:grid-cols-[minmax(0,630px)_250px] lg:gap-10 xl:gap-16 px-0 sm:px-4 py-0 sm:py-6">
      <div>
      <div className="px-4 pt-5 sm:px-0 sm:pt-0 flex items-end justify-between">
        <div>
          <p className="fitcore-kicker text-[9px] text-primary font-bold">{t('explore')}</p>
          <h1 className="text-[28px] leading-none mt-1">{t('home')}</h1>
        </div>
        <button data-testid="feed-create-post-button" onClick={() => window.dispatchEvent(new CustomEvent('fitcore:opencomposer'))} className="lg:hidden w-10 h-10 flex items-center justify-center rounded-full bg-foreground text-background active:scale-95 transition-transform" aria-label={t('create')}><Plus size={20}/></button>
      </div>
      {/* Stories rail at the very top */}
      <StoriesBar />

      <div className="px-3 sm:px-0 mt-1 space-y-4">
        <SuggestedUsers />
        {/* Inline composer only on desktop; mobile uses the + button */}
        <div className="hidden lg:block">
          <PostComposer onPosted={onNewPost} />
        </div>
      </div>

      {loading ? (
        <div className="space-y-4 mt-4 px-3 sm:px-0">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-card rounded-xl border border-border p-4 space-y-3">
              <div className="flex items-center gap-3">
                <Skeleton className="w-9 h-9 rounded-full" />
                <div className="space-y-1.5"><Skeleton className="h-3 w-32" /><Skeleton className="h-2.5 w-20" /></div>
              </div>
              <Skeleton className="h-72 w-full rounded-lg" />
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-3 sm:space-y-5 mt-3" data-testid="feed-posts-list">
          {items.map((item) =>
            item.type === 'post' ? (
              <PostCard key={item.key} post={item.data} onDeleted={(postId) => setPosts((current) => current.filter((post) => post.id !== postId))} />
            ) : (
              <div key={item.key} className="px-3 sm:px-0"><SponsoredCard ad={item.data} /></div>
            )
          )}
          {posts.length === 0 && (
            <div className="text-center py-16 text-muted-foreground">
              <Camera size={40} className="mx-auto mb-3 opacity-40" strokeWidth={1.4} />
              <p className="text-sm" data-testid="feed-empty-state">{t('noPostsYet')}</p>
            </div>
          )}
        </div>
      )}
      </div>
      <aside className="hidden lg:block pt-20">
        <div className="sticky top-8 space-y-4">
          <div className="rounded-3xl border border-border bg-card p-5 elev-1">
            <div className="w-9 h-9 rounded-xl bg-accent flex items-center justify-center text-foreground mb-4"><Sparkles size={18}/></div>
            <p className="fitcore-kicker text-[9px] text-muted-foreground">{t('aiHub')}</p>
            <h2 className="text-xl leading-tight mt-1">{t('createPlan')}</h2>
            <button data-testid="feed-sidebar-ai-button" onClick={() => navigate('/ai')} className="mt-4 text-sm font-extrabold text-primary hover:underline">{t('aiCoach')}</button>
          </div>
          <div className="px-2 text-[11px] leading-6 text-muted-foreground">
            <span className="font-bold text-foreground">FitCore</span>
          </div>
        </div>
      </aside>
    </div>
  );
}
